import { useState } from "react";
import { Control, useWatch } from "react-hook-form";
import { ModuSignStartDocumentRequest } from "src/api/contract/modusign-types";
import { BaseButton, BaseInput } from "src/components";

type Props = {
  control: Control<ModuSignStartDocumentRequest>;
  verifyIdx: number;
  onClose: () => void;
  setValueVerification: (verification: { value: string; hint: string }) => void;
};

const ModuSignPasswordVerify = ({ control, verifyIdx, onClose, setValueVerification }: Props) => {
  const [passwordHidden, setPasswordHidden] = useState(true);

  const verificationValue = useWatch({
    control,
    name: `document.participantMappingsList.${verifyIdx}.verification.password`,
  });

  const [verification, setVerification] = useState({
    value: verificationValue?.value || "",
    hint: verificationValue?.hint || "",
  });

  const [error, setError] = useState({
    valueError: "",
    hintError: "",
  });

  // 기존 Controller rules를 그대로 옮긴 검증 함수들
  const validateValue = (value: string): string => {
    if (!value) {
      return "필수입력 항목입니다.";
    }
    if (value.length < 4 || value.length > 20) {
      return "4자 이상 20자 이내로 입력해주세요.";
    }
    return "";
  };

  const validateHint = (value: string): string => {
    if (value.length > 30) {
      return "30자 이내로 입력해 주세요.";
    }

    return "";
  };

  // 모든 필드 검증
  const validateAll = (): boolean => {
    const valueError = validateValue(verification.value);
    const hintError = validateHint(verification.hint);

    setError({
      valueError,
      hintError,
    });

    return !valueError && !hintError;
  };

  const handleSubmit = () => {
    const isValid = validateAll();
    if (isValid) {
      setValueVerification(verification);
      onClose();
    }
  };

  return (
    <>
      <section className="base-abstract-modal__title">
        <h1>받는 사람의 비밀번호 인증 추가</h1>
      </section>
      <section className="base-abstract-modal__contents mx30">
        <ul className="base-list">
          <li>설정한 암호는 반드시 미리 서명자에게 전달해 주세요.</li>
          <li>해당 암호는 이후 확인하거나 변경할 수 없습니다.</li>
          <li>문서 열람 시 암호 입력이 필요하며, 대소문자를 구분합니다.</li>
        </ul>
        <div className="contents-container mt20">
          <section className="contents-container__grid ">
            <div className="contents-container__grid-index">
              <p className="required">문서 열람 비밀번호</p>
            </div>
            <div className="contents-container__grid-contents">
              <BaseInput
                onIconClick={() => {
                  setPasswordHidden(!passwordHidden);
                }}
                rightIconClassName={passwordHidden ? "ic-eyes-close" : "ic-eyes-open"}
                className={"ic-eyes-open"}
                placeholder="4자 이상 20자 이내로 입력해주세요."
                type={passwordHidden ? "password" : "text"}
                value={verification.value}
                onChange={(value: string) => {
                  setVerification({ ...verification, value });

                  if (error.valueError) {
                    setError((prev) => ({ ...prev, valueError: validateValue(value) }));
                  }
                }}
                errorText={error?.valueError}
              />
            </div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p>비밀번호 힌트</p>
            </div>
            <div className="contents-container__grid-contents">
              <BaseInput
                placeholder="30자 이내로 입력해 주세요.(선택입력)"
                onChange={(value: string) => {
                  setVerification({ ...verification, hint: value });

                  if (error.hintError) {
                    setError((prev) => ({ ...prev, hintError: validateHint(value) }));
                  } else {
                    setError((prev) => ({ ...prev, hintError: "" }));
                  }
                }}
                value={verification.hint}
                errorText={error?.hintError}
              />
            </div>
          </section>
        </div>
      </section>
      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title="닫기" className="color-white" onClick={onClose} />
        <BaseButton title="인증추가" onClick={handleSubmit} />
      </section>
    </>
  );
};

export default ModuSignPasswordVerify;
